import { useEffect, useRef, useState } from 'react';
import { type PlaybackState, type ReadyStatus } from '../lib/api';
import {
  applyLocalAudio,
  loadLocalVolume,
  subscribeLocalAudio,
  VOLUME_GESTURE_EVENT,
} from '../lib/localVolume';
import { isYouTubeVideoId } from '../lib/youtube';
import './SyncPlayer.css';

const YT_ENDED = 0;
const YT_PLAYING = 1;
const YT_PAUSED = 2;
const YT_BUFFERING = 3;
const YT_CUED = 5;
const SOFT_DRIFT = 1.5;
const HARD_DRIFT = 3;
const SEEK_COOLDOWN_MS = 8000;
const CANNOT_PLAY = 'This YouTube video cannot be played in the Zemew theater.';

let youtubeApiPromise: Promise<void> | null = null;

function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      document.head.appendChild(tag);
    }
    if (window.YT?.Player) resolve();
  });

  return youtubeApiPromise;
}

function safeGetTime(player: YT.Player | null): number | null {
  try {
    const t = player?.getCurrentTime();
    return typeof t === 'number' && Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
}

function safeGetDuration(player: YT.Player | null): number | null {
  try {
    const d = player?.getDuration();
    return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : null;
  } catch {
    return null;
  }
}

function isPlayerEnded(player: YT.Player | null): boolean {
  try {
    return player?.getPlayerState() === YT_ENDED;
  } catch {
    return false;
  }
}

function forceTheaterAudio(
  player: YT.Player | null,
  volume: number,
  muted: boolean,
): void {
  applyLocalAudio(player, volume, muted);
}

function expectedPosition(state: PlaybackState, clockOffset: number): number {
  const snapshot = Math.max(0, state.position_seconds);
  if (state.phase !== 'playing' || !state.is_playing) return snapshot;
  const stamped =
    state.server_time_ms || (state.server_time ? new Date(state.server_time).getTime() : 0);
  if (!stamped) return snapshot;
  const serverNow = Date.now() + clockOffset;
  return Math.max(0, snapshot + (serverNow - stamped) / 1000);
}

export default function SyncPlayer({
  playback,
  isAdmin,
  correction,
  onClearCorrection,
  onHeartbeat,
  onLocalEnded,
  onJoinedWatching,
  onReadyStatus,
  clockOffset,
  onStartAnyway,
  onSkip,
  onRemoveRequest,
}: {
  playback: PlaybackState;
  isAdmin: boolean;
  correction: { position_seconds: number; is_playing: boolean } | null;
  onClearCorrection: () => void;
  onHeartbeat: (position: number, extra?: { ended?: boolean; duration?: number }) => void;
  onLocalEnded: (info: { position?: number; playerDuration?: number }) => void;
  onJoinedWatching?: () => void;
  onReadyStatus: (sessionId: string, status: ReadyStatus, errorCode?: number) => void;
  clockOffset: number;
  onStartAnyway?: () => void;
  onSkip?: () => void;
  onRemoveRequest?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const applyingRef = useRef(false);
  const readyRef = useRef(false);
  const lastEndedAtRef = useRef(0);
  const onLocalEndedRef = useRef(onLocalEnded);
  onLocalEndedRef.current = onLocalEnded;
  const onHeartbeatRef = useRef(onHeartbeat);
  onHeartbeatRef.current = onHeartbeat;
  const primedRef = useRef(false);
  const lastReadyRef = useRef<string>('');
  const lastSeekAtRef = useRef(0);
  const softDriftRef = useRef(0);
  const playbackRef = useRef(playback);
  playbackRef.current = playback;
  const offsetRef = useRef(clockOffset);
  offsetRef.current = clockOffset;
  const onReadyStatusRef = useRef(onReadyStatus);
  onReadyStatusRef.current = onReadyStatus;

  const [error, setError] = useState<string | null>(null);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [localReady, setLocalReady] = useState(false);
  const [countdownLeft, setCountdownLeft] = useState<number | null>(null);
  const [catchingUp, setCatchingUp] = useState(false);
  const [volume, setVolume] = useState(() => loadLocalVolume().volume);
  const [muted, setMuted] = useState(() => loadLocalVolume().muted);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  volumeRef.current = volume;
  mutedRef.current = muted;

  const videoId = playback.movie?.youtube_video_id;
  const movieId = playback.movie?.id;
  const sessionId = playback.session_id;
  const phase = playback.phase ?? (playback.is_playing ? 'playing' : 'paused');

  function seekIfNeeded(player: YT.Player, target: number, force = false): boolean {
    const current = safeGetTime(player);
    if (current == null) return false;
    const drift = Math.abs(current - target);
    if (drift < HARD_DRIFT) return false;
    const now = Date.now();
    if (!force && now - lastSeekAtRef.current < SEEK_COOLDOWN_MS && drift < 8) return false;
    lastSeekAtRef.current = now;
    applyingRef.current = true;
    try {
      player.seekTo(target, true);
    } catch {
      setNeedsGesture(true);
      applyingRef.current = false;
      return false;
    }
    window.setTimeout(() => {
      applyingRef.current = false;
    }, 600);
    return true;
  }

  function report(statusValue: ReadyStatus, errorCode?: number) {
    const sid = playbackRef.current.session_id;
    if (!sid) return;
    const key = `${sid}:${statusValue}:${errorCode ?? ''}`;
    if (lastReadyRef.current === key && statusValue !== 'BUFFERING') return;
    lastReadyRef.current = key;
    onReadyStatusRef.current(sid, statusValue, errorCode);
    if (statusValue === 'READY') setLocalReady(true);
    if (statusValue === 'LOADING' || statusValue === 'FAILED') setLocalReady(false);
  }

  function reportEnded(player: YT.Player | null = playerRef.current) {
    const now = Date.now();
    if (now - lastEndedAtRef.current < 2500) return;
    lastEndedAtRef.current = now;
    onLocalEndedRef.current({
      position: safeGetTime(player) ?? undefined,
      playerDuration: safeGetDuration(player) ?? undefined,
    });
  }

  function pauseForPrepare(player: YT.Player) {
    applyingRef.current = true;
    try {
      const target = expectedPosition(playbackRef.current, offsetRef.current);
      player.seekTo(target, true);
      player.pauseVideo();
    } catch {
      // ignore
    }
    window.setTimeout(() => {
      applyingRef.current = false;
    }, 400);
  }

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !isYouTubeVideoId(videoId)) {
      setError(videoId ? CANNOT_PLAY : null);
      return;
    }

    let cancelled = false;
    lastEndedAtRef.current = 0;
    readyRef.current = false;
    primedRef.current = false;
    lastReadyRef.current = '';
    lastSeekAtRef.current = 0;
    setPlayerReady(false);
    setLocalReady(false);
    setError(null);
    setNeedsGesture(true);
    setCatchingUp(false);
    report('LOADING');

    const mount = document.createElement('div');
    host.innerHTML = '';
    host.appendChild(mount);

    void loadYouTubeApi()
      .then(() => {
        if (cancelled || !window.YT?.Player) return;
        const startSeconds = Math.max(0, playbackRef.current.position_seconds);
        const player = new window.YT.Player(mount, {
          videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            origin: window.location.origin,
            enablejsapi: 1,
            start: Math.floor(startSeconds),
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              playerRef.current = event.target;
              readyRef.current = true;
              setPlayerReady(true);
              try {
                forceTheaterAudio(event.target, volumeRef.current, mutedRef.current);
                event.target.cueVideoById({ videoId: videoId!, startSeconds });
                event.target.pauseVideo();
              } catch {
                pauseForPrepare(event.target);
              }
              report('CUED');
            },
            onStateChange: (event) => {
              if (cancelled) return;
              const state = playbackRef.current;
              const preparing =
                state.phase === 'preparing' ||
                state.phase === 'seeking' ||
                state.phase === 'countdown';

              if (event.data === YT_ENDED) {
                if (state.phase === 'playing' || state.phase === 'paused') {
                  reportEnded(event.target);
                }
                return;
              }

              if (event.data === YT_BUFFERING) {
                if (preparing) report('CUED');
                else if (state.phase === 'playing') {
                  setCatchingUp(true);
                }
                return;
              }

              if (event.data === YT_CUED || event.data === YT_PAUSED) {
                if (preparing) {
                  report('READY');
                  return;
                }
                if (state.phase === 'playing' && !applyingRef.current) {
                  const duration = safeGetDuration(event.target);
                  const current = safeGetTime(event.target);
                  if (duration && current != null && current >= duration - 2) {
                    reportEnded(event.target);
                    return;
                  }
                  try {
                    event.target.playVideo();
                  } catch {
                    setNeedsGesture(true);
                  }
                }
              }

              if (event.data === YT_PLAYING) {
                forceTheaterAudio(event.target, volumeRef.current, mutedRef.current);
                setNeedsGesture(false);
                setCatchingUp(false);
                if (preparing && !applyingRef.current) {
                  pauseForPrepare(event.target);
                  report('READY');
                  return;
                }
                if (state.phase === 'playing' && !applyingRef.current) {
                  const target = expectedPosition(state, offsetRef.current);
                  seekIfNeeded(event.target, target);
                }
              }
            },
            onError: (event) => {
              setError(CANNOT_PLAY);
              report('FAILED', event.data);
            },
          },
        });
        playerRef.current = player;
      })
      .catch(() => {
        if (!cancelled) {
          setError(CANNOT_PLAY);
          report('FAILED');
        }
      });

    return () => {
      cancelled = true;
      readyRef.current = false;
      try {
        playerRef.current?.destroy();
      } catch {
        // ignore
      }
      playerRef.current = null;
      host.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, movieId]);

  useEffect(() => {
    lastReadyRef.current = '';
    lastSeekAtRef.current = 0;
    setLocalReady(false);
    if (phase === 'preparing' || phase === 'seeking') {
      report('LOADING');
      const player = playerRef.current;
      if (player && playerReady) pauseForPrepare(player);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!playerReady || (phase !== 'preparing' && phase !== 'seeking')) return;
    const id = window.setInterval(() => {
      const player = playerRef.current;
      if (!player) return;
      try {
        const st = player.getPlayerState();
        const data = player.getVideoData?.();
        if (data?.video_id && videoId && data.video_id !== videoId) return;
        if (st === YT_CUED || st === YT_PAUSED) report('READY');
        else if (st === YT_PLAYING) {
          pauseForPrepare(player);
          report('READY');
        } else if (st === YT_BUFFERING) report('CUED');
      } catch {
        // ignore
      }
    }, 400);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerReady, phase, sessionId, videoId]);

  useEffect(() => {
    if (phase !== 'countdown' || !playback.play_at_ms) {
      setCountdownLeft(null);
      return;
    }
    const tick = () => {
      const ms = playback.play_at_ms! - (Date.now() + offsetRef.current);
      setCountdownLeft(ms);
    };
    tick();
    const id = window.setInterval(tick, 50);
    return () => window.clearInterval(id);
  }, [phase, playback.play_at_ms]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady || phase !== 'countdown' || !playback.play_at_ms) return;
    let cancelled = false;
    let timeout = 0;

    const wait = () => {
      if (cancelled) return;
      const ms = playback.play_at_ms! - (Date.now() + offsetRef.current);
      if (ms <= 20) {
        applyingRef.current = true;
        try {
          player.seekTo(Math.max(0, playback.position_seconds), true);
          if (primedRef.current) {
            forceTheaterAudio(player, volumeRef.current, mutedRef.current);
          }
          player.playVideo();
          setNeedsGesture(!primedRef.current);
        } catch {
          setNeedsGesture(true);
        }
        window.setTimeout(() => {
          applyingRef.current = false;
        }, 500);
        return;
      }
      timeout = window.setTimeout(wait, Math.min(ms, 40));
    };
    wait();
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [phase, playback.play_at_ms, playback.position_seconds, playback.session_id, playerReady]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) return;
    if (phase === 'countdown' || phase === 'preparing' || phase === 'seeking') return;
    if (phase === 'playing' && playback.play_at_ms) {
      const sinceStart = Date.now() + offsetRef.current - playback.play_at_ms;
      if (sinceStart >= 0 && sinceStart < 2000) return;
    }

    if (isPlayerEnded(player)) {
      reportEnded(player);
      return;
    }

    applyingRef.current = true;
    try {
      const target = expectedPosition(playback, clockOffset);
      seekIfNeeded(player, target, true);
      if (phase === 'playing' && playback.is_playing) {
        forceTheaterAudio(player, volumeRef.current, mutedRef.current);
        player.playVideo();
      } else {
        player.pauseVideo();
      }
    } catch {
      setNeedsGesture(true);
    }
    window.setTimeout(() => {
      applyingRef.current = false;
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playback.is_playing, playback.phase, playback.session_id, playerReady]);

  useEffect(() => {
    if (!correction || !playerRef.current || !playerReady) return;
    if (phase === 'preparing' || phase === 'seeking' || phase === 'countdown') {
      onClearCorrection();
      return;
    }
    const player = playerRef.current;
    if (isPlayerEnded(player)) {
      reportEnded(player);
      onClearCorrection();
      return;
    }
    const current = safeGetTime(player);
    const target = correction.position_seconds;
    if (current != null && Math.abs(current - target) < HARD_DRIFT) {
      onClearCorrection();
      return;
    }
    applyingRef.current = true;
    try {
      lastSeekAtRef.current = Date.now();
      player.seekTo(target, true);
      if (correction.is_playing) player.playVideo();
      else player.pauseVideo();
    } catch {
      setNeedsGesture(true);
    }
    onClearCorrection();
    window.setTimeout(() => {
      applyingRef.current = false;
    }, 600);
  }, [correction, onClearCorrection, playerReady, phase]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const player = playerRef.current;
      if (!player || !readyRef.current || applyingRef.current) return;
      const current = safeGetTime(player);
      if (current == null) return;
      const duration = safeGetDuration(player);
      const ended = isPlayerEnded(player);
      onHeartbeatRef.current(current, {
        ended,
        duration: duration ?? undefined,
      });

      if (ended) {
        reportEnded(player);
        softDriftRef.current = 0;
        return;
      }

      const state = playbackRef.current;
      if (state.phase !== 'playing' || !state.is_playing) {
        softDriftRef.current = 0;
        return;
      }
      const target = expectedPosition(state, offsetRef.current);
      const drift = Math.abs(current - target);
      if (drift < SOFT_DRIFT) {
        softDriftRef.current = 0;
        return;
      }
      if (drift < HARD_DRIFT) {
        softDriftRef.current += 1;
        if (softDriftRef.current < 3) return;
      }
      softDriftRef.current = 0;
      if (seekIfNeeded(player, target)) {
        try {
          player.playVideo();
        } catch {
          setNeedsGesture(true);
        }
      }
    }, 5000);
    return () => window.clearInterval(id);
  }, [onHeartbeat]);

  useEffect(() => {
    return subscribeLocalAudio((audio) => {
      setVolume(audio.volume);
      setMuted(audio.muted);
    });
  }, []);

  useEffect(() => {
    if (!playerReady) return;
    applyLocalAudio(playerRef.current, volume, muted);
  }, [volume, muted, playerReady]);

  useEffect(() => {
    const onGesture = () => {
      if (needsGesture) void primePlayer();
    };
    window.addEventListener(VOLUME_GESTURE_EVENT, onGesture);
    return () => window.removeEventListener(VOLUME_GESTURE_EVENT, onGesture);
  }, [needsGesture]);

  async function primePlayer() {
    const player = playerRef.current;
    if (!player) return;
    if (isPlayerEnded(player) && playbackRef.current.phase === 'playing') {
      reportEnded(player);
      return;
    }
    primedRef.current = true;
    applyingRef.current = true;
    try {
      forceTheaterAudio(player, volumeRef.current, mutedRef.current);
      const state = playbackRef.current;
      if (state.phase === 'playing' && state.is_playing) {
        player.seekTo(expectedPosition(state, offsetRef.current), true);
        player.playVideo();
      } else {
        player.playVideo();
        player.pauseVideo();
        player.seekTo(Math.max(0, state.position_seconds), true);
      }
      setNeedsGesture(false);
      onJoinedWatching?.();
      const st = player.getPlayerState();
      if (
        (state.phase === 'preparing' || state.phase === 'seeking' || state.phase === 'countdown') &&
        (st === YT_CUED || st === YT_PAUSED || st === YT_PLAYING)
      ) {
        report('READY');
      }
    } catch {
      setNeedsGesture(true);
    } finally {
      window.setTimeout(() => {
        applyingRef.current = false;
      }, 400);
    }
  }

  if (!playback.movie) return null;

  const preparing = phase === 'preparing' || phase === 'seeking';
  const countdown = phase === 'countdown';
  const failed = phase === 'failed';
  const lateJoin =
    (phase === 'playing' || phase === 'paused') && !localReady && !error && playerReady === false;
  const readiness = playback.readiness;
  const countdownSec =
    countdownLeft != null ? Math.max(1, Math.ceil(countdownLeft / 1000)) : null;
  const overlayTitle = failed
    ? CANNOT_PLAY
    : countdown && countdownLeft != null && countdownLeft <= 0
      ? 'STARTING'
      : countdown
        ? 'STARTING IN'
        : preparing
          ? phase === 'seeking'
            ? 'SEEKING...'
            : 'PREPARING THEATER...'
          : catchingUp || lateJoin
            ? 'SYNCING TO THEATER...'
            : needsGesture
              ? 'TAP TO WATCH'
              : null;

  return (
    <div className="sync-player panel fade-up">
      <div className="sync-video-wrap">
        <div className="sync-youtube" ref={hostRef} />
        {overlayTitle && (
          <div
            className={`prepare-overlay${countdown ? ' is-countdown' : ''}${
              catchingUp || lateJoin ? ' is-syncing' : ''
            }${needsGesture && !error ? ' is-unlock' : ''}`}
            onClick={(event) => {
              if (!needsGesture || error) return;
              if ((event.target as HTMLElement).closest('button')) return;
              void primePlayer();
            }}
          >
            <div className="prepare-kicker">{overlayTitle}</div>
            {countdown && countdownSec != null && countdownLeft != null && countdownLeft > 0 && (
              <div className="prepare-count">{countdownSec}</div>
            )}
            {(preparing || countdown) && readiness && (
              <>
                <ul className="prepare-list">
                  {readiness.viewers.map((viewer) => (
                    <li key={viewer.userId} className={`prepare-row is-${viewer.status.toLowerCase()}`}>
                      <span className="prepare-name">
                        {viewer.displayName || viewer.username}
                      </span>
                      <span className="prepare-state">
                        {viewer.status === 'READY'
                          ? '✓ READY'
                          : viewer.status === 'FAILED'
                            ? '× FAILED'
                            : viewer.status === 'CUED'
                              ? '● CUED'
                              : viewer.status === 'BUFFERING'
                                ? '● BUFFERING'
                                : '● LOADING'}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="prepare-tally">
                  {readiness.ready} / {readiness.required} READY
                </div>
              </>
            )}
            {failed && (
              <>
                <p className="prepare-fail">{playback.error || CANNOT_PLAY}</p>
                <p className="prepare-next">Skipping to the next video…</p>
              </>
            )}
            {needsGesture && !error && (
              <span className="join-overlay-sub">Tap to turn on sound</span>
            )}
            {isAdmin && preparing && (
              <button className="btn btn-gold" type="button" onClick={() => onStartAnyway?.()}>
                Start Anyway
              </button>
            )}
            {isAdmin && failed && (
              <div className="prepare-actions">
                <button className="btn" type="button" onClick={() => onRemoveRequest?.()}>
                  Remove Request
                </button>
                <button className="btn btn-gold" type="button" onClick={() => onSkip?.()}>
                  Skip to Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
